/**
 * V1.6.3 — Sélecteur de paroisse active dans le sidebar.
 *
 * Affichage conditionnel :
 *  - User sans paroisse (admin global, edaci_admin sans choix) → "Choisir une paroisse"
 *  - User avec 1 seule paroisse → badge "info" (nom + rôle), pas de dropdown
 *  - User avec N paroisses (cure qui dépanne, vicaire partagé) → dropdown
 *    avec liste des affectations + étoile sur la primaire + click switch
 *
 * Mode collapsed : icône Building2 avec tooltip nom paroisse.
 *
 * Comportement switch :
 *  - Call setCurrentParish(id) du ParishContext
 *  - Persistance localStorage automatique
 *  - Tous les composants qui useCurrentParishId() se re-render avec le nouvel id
 *  - Toutes les useQuery(parishId) sont invalidées et refetch sur le nouveau parish
 */
import { useLocation } from "wouter";
import {
  Building2,
  Star,
  ChevronDown,
  Check,
  ArrowRightLeft,
} from "lucide-react";

import { useParish } from "@/contexts/parish-context";
import { useParishInfo } from "@/hooks/useParishInfo";
import { useTranslation } from "@/lib/i18n/i18n";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface ParishSwitcherProps {
  collapsed: boolean;
}

export function ParishSwitcher({ collapsed }: ParishSwitcherProps) {
  const t = useTranslation();
  const [, navigate] = useLocation();
  const {
    availableParishes,
    currentParishId,
    currentParishName,
    isGlobalAdmin,
    setCurrentParish,
  } = useParish();
  // Administrateur diocésain (19/10/2026) : la paroisse choisie n'est pas
  // dans ses appartenances, son nom se lit sur la fiche de la paroisse.
  const { data: infoParoisse } = useParishInfo(
    isGlobalAdmin && currentParishId && !currentParishName ? currentParishId : null,
  );
  const nomParoisse = currentParishName ?? infoParoisse?.nom ?? infoParoisse?.name ?? null;

  // Cas 1 : aucune paroisse active ET pas admin → ne rien afficher
  // (Le ProtectedRoute aura redirigé vers /select-parish avant d'arriver ici.)
  if (!currentParishId && availableParishes.length === 0 && !isGlobalAdmin) {
    return null;
  }

  // Cas 2 : admin global sans paroisse choisie (rare, défensif)
  if (!currentParishId && isGlobalAdmin) {
    return (
      <button
        onClick={() => navigate("/select-parish")}
        className="w-full px-2 py-2 text-xs font-medium text-brand-info-blue hover:bg-brand-info-blue/5 border-y border-dashed border-brand-info-blue/30 transition-colors"
        data-testid="parish-switcher-select"
      >
        {t("common.parishSwitcher.choosePrompt")}
      </button>
    );
  }

  const multi = availableParishes.length > 1 || isGlobalAdmin;

  // Mode collapsed : juste icône avec tooltip
  if (collapsed) {
    return (
      <div
        className="px-2 py-2 border-b flex justify-center"
        style={{ borderColor: "rgba(0,0,0,0.07)" }}
        title={nomParoisse ?? ""}
        data-testid="parish-switcher-collapsed"
      >
        <Building2 className="w-4 h-4 text-brand-gold" />
      </div>
    );
  }

  // Cas 3 : 1 seule paroisse → affichage info pur (pas de dropdown)
  if (!multi) {
    return (
      <div
        className="px-2 py-2 border-b"
        style={{ borderColor: "rgba(0,0,0,0.07)" }}
        data-testid="parish-switcher-single"
      >
        <div className="flex items-center gap-2 px-1 py-1.5 rounded-lg bg-brand-gold/8 border border-brand-gold/20">
          <Building2 className="w-3.5 h-3.5 text-brand-gold shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-xs font-bold text-brand-primary truncate">
              {nomParoisse}
            </div>
            <div className="text-xs text-gray-400">
              {t("common.parishSwitcher.activeParish")}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Cas 4 : N paroisses → dropdown switch
  return (
    <div
      className="px-2 py-2 border-b"
      style={{ borderColor: "rgba(0,0,0,0.07)" }}
      data-testid="parish-switcher-multi"
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg bg-brand-gold/8 border border-brand-gold/20 hover:bg-brand-gold/15 transition-colors text-left"
            data-testid="parish-switcher-trigger"
          >
            <Building2 className="w-3.5 h-3.5 text-brand-gold shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-xs font-bold text-brand-primary truncate">
                {nomParoisse ?? t("common.parishSwitcher.choosePrompt")}
              </div>
              <div className="text-xs text-gray-400 flex items-center gap-1">
                <ArrowRightLeft className="w-2.5 h-2.5" />
                {isGlobalAdmin
                  ? t("common.parishSwitcher.adminChange")
                  : t("common.parishSwitcher.nParishes", { n: availableParishes.length })}
              </div>
            </div>
            <ChevronDown className="w-3 h-3 text-gray-400 shrink-0" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="w-64"
          data-testid="parish-switcher-menu"
        >
          {/* « Vos paroisses » seulement s'il y en a : un administrateur
              diocésain n'a pas d'affectation, la rubrique était vide
              (recette du 19/10/2026). */}
          {availableParishes.length > 0 && (
            <>
              <DropdownMenuLabel className="text-xs font-bold uppercase tracking-wider text-gray-400">
                {t("common.parishSwitcher.menuTitle")}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
            </>
          )}
          {availableParishes.map((p) => {
            const isCurrent = p.id === currentParishId;
            return (
              <DropdownMenuItem
                key={p.id}
                onClick={() => setCurrentParish(p.id)}
                className="cursor-pointer flex items-center gap-2 py-2"
                data-testid={`parish-switcher-item-${p.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] font-semibold text-brand-primary truncate flex items-center gap-1.5">
                    {p.name}
                    {p.isPrimary && (
                      <Star
                        className="w-3 h-3 text-brand-gold fill-brand-gold shrink-0"
                      />
                    )}
                  </div>
                  <div className="text-xs text-gray-400">{p.role}</div>
                </div>
                {isCurrent && (
                  <Check className="w-4 h-4 text-brand-success shrink-0" />
                )}
              </DropdownMenuItem>
            );
          })}
          {isGlobalAdmin && (
            <>
              {availableParishes.length > 0 && <DropdownMenuSeparator />}
              <DropdownMenuItem
                onClick={() => navigate("/select-parish")}
                className="cursor-pointer text-[12px] text-brand-info-blue"
                data-testid="parish-switcher-go-select"
              >
                <ArrowRightLeft className="w-3.5 h-3.5 mr-2" />
                {t("common.parishSwitcher.openSelect")}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
